import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Ticket, MessageCircle, Clock, CheckCircle, 
  AlertCircle, Send, Loader2, Filter, Trash2,
  User, ChevronDown, ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTickets = () => {
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filter, setFilter] = useState('all');
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);

  useEffect(() => {
    const loadTickets = async () => {
      try {
        const params = filter !== 'all' ? `?status=${filter}` : '';
        const response = await axios.get(`${API_URL}/api/admin/tickets${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTickets(response.data.tickets);
        setStats(response.data.stats);
      } catch (error) {
        toast.error('Talepler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    loadTickets();
  }, [filter, token]);

  const fetchTickets = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API_URL}/api/admin/tickets${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets);
      setStats(response.data.stats);
    } catch (error) {
      toast.error('Talepler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) return;

    setReplying(true);
    try {
      await axios.post(`${API_URL}/api/admin/tickets/${selectedTicket.id}/reply`, 
        { message: replyMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Yanıt gönderildi');
      setReplyMessage('');
      // Refresh
      const response = await axios.get(`${API_URL}/api/admin/tickets/${selectedTicket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(response.data);
      fetchTickets();
    } catch (error) {
      toast.error('Yanıt gönderilemedi');
    } finally {
      setReplying(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/tickets/${ticketId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Durum güncellendi');
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const handleDelete = async (ticketId) => {
    if (!window.confirm('Bu talebi silmek istediğinize emin misiniz?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Talep silindi');
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      toast.error('Talep silinemedi');
    }
  };

  const getStatusBadge = (status) => {
    const statuses = {
      open: { label: 'Açık', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      in_progress: { label: 'İşleniyor', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
      resolved: { label: 'Çözüldü', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      closed: { label: 'Kapalı', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
    };
    const s = statuses[status] || statuses.open;
    return <Badge className={`${s.color} border`}>{s.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const priorities = {
      low: { label: 'Düşük', color: 'bg-slate-500/20 text-slate-400' },
      normal: { label: 'Normal', color: 'bg-blue-500/20 text-blue-400' },
      high: { label: 'Yüksek', color: 'bg-red-500/20 text-red-400' }
    };
    const p = priorities[priority] || priorities.normal;
    return <Badge className={p.color}>{p.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Stats */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Ticket className="h-7 w-7 text-primary" />
          Destek Talepleri
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-white/5 border-white/10">
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-white">{stats.total || 0}</div>
            <div className="text-xs text-slate-400">Toplam</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/20">
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-blue-400">{stats.open || 0}</div>
            <div className="text-xs text-slate-400">Açık</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-500/10 border-amber-500/20">
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-amber-400">{stats.in_progress || 0}</div>
            <div className="text-xs text-slate-400">İşleniyor</div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/20">
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-green-400">{stats.resolved || 0}</div>
            <div className="text-xs text-slate-400">Çözüldü</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-500/10 border-slate-500/20">
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-slate-400">{stats.closed || 0}</div>
            <div className="text-xs text-slate-400">Kapalı</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { value: 'all', label: 'Tümü' },
          { value: 'open', label: 'Açık' },
          { value: 'in_progress', label: 'İşleniyor' },
          { value: 'resolved', label: 'Çözüldü' },
          { value: 'closed', label: 'Kapalı' }
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              filter === f.value
                ? 'bg-primary text-brand-dark'
                : 'bg-white/5 text-slate-300 hover:bg-white/10'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Ticket List */}
        <div className="space-y-3">
          {tickets.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Ticket className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Bu filtreye uygun talep yok</p>
              </CardContent>
            </Card>
          ) : (
            tickets.map((ticket) => (
              <Card 
                key={ticket.id}
                className={`cursor-pointer transition-all ${
                  selectedTicket?.id === ticket.id 
                    ? 'border-primary ring-1 ring-primary' 
                    : 'hover:border-white/20'
                }`}
                onClick={() => setSelectedTicket(ticket)}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <span className="text-xs text-slate-500">{ticket.ticket_number}</span>
                    <div className="flex gap-1">
                      {getPriorityBadge(ticket.priority)}
                      {getStatusBadge(ticket.status)}
                    </div>
                  </div>
                  <h3 className="text-white font-medium mb-1 line-clamp-1">{ticket.subject}</h3>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <User className="h-3 w-3" />
                    <span>{ticket.user_email}</span>
                    <span>•</span>
                    <span>{new Date(ticket.updated_at).toLocaleDateString('tr-TR')}</span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Ticket Detail */}
        <div>
          {selectedTicket ? (
            <Card className="sticky top-4">
              <CardHeader className="border-b border-white/5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-slate-500">{selectedTicket.ticket_number}</span>
                      {getStatusBadge(selectedTicket.status)}
                    </div>
                    <CardTitle className="text-lg">{selectedTicket.subject}</CardTitle>
                    <p className="text-sm text-slate-400 mt-1">
                      {selectedTicket.user_name} ({selectedTicket.user_email})
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-400 hover:text-red-300"
                    onClick={() => handleDelete(selectedTicket.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Status Actions */}
                <div className="flex gap-2 mt-4">
                  {['open', 'in_progress', 'resolved', 'closed'].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(selectedTicket.id, status)}
                      disabled={selectedTicket.status === status}
                      className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                        selectedTicket.status === status
                          ? 'bg-primary text-brand-dark'
                          : 'bg-white/5 text-slate-400 hover:bg-white/10'
                      }`}
                    >
                      {status === 'open' && 'Açık'}
                      {status === 'in_progress' && 'İşleniyor'}
                      {status === 'resolved' && 'Çözüldü'}
                      {status === 'closed' && 'Kapalı'}
                    </button>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
                {/* Messages */}
                <div className="space-y-3 mb-4">
                  {selectedTicket.messages?.map((msg, index) => (
                    <div
                      key={msg.id || index}
                      className={`p-3 rounded-lg text-sm ${
                        msg.sender === 'admin'
                          ? 'bg-primary/10 border border-primary/20 ml-4'
                          : 'bg-white/5 border border-white/10 mr-4'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium ${msg.sender === 'admin' ? 'text-primary' : 'text-white'}`}>
                          {msg.sender === 'admin' ? 'Siz' : msg.sender_name || 'Kullanıcı'}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(msg.created_at).toLocaleString('tr-TR')}
                        </span>
                      </div>
                      <p className="text-slate-300 whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  ))}
                </div>

                {/* Reply Form */}
                {selectedTicket.status !== 'closed' && (
                  <form onSubmit={handleReply} className="border-t border-white/5 pt-4">
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Yanıtınızı yazın..."
                      className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-primary/50 resize-none text-sm"
                      rows={3}
                    />
                    <div className="flex justify-end mt-2">
                      <Button type="submit" size="sm" disabled={replying || !replyMessage.trim()}>
                        {replying ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="mr-2 h-4 w-4" />
                        )}
                        Yanıtla
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageCircle className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Detayları görüntülemek için bir talep seçin</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTickets;
